import { type FC, useEffect, useState } from "react";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { Alert } from "../common/Alert";
import { LoadingSpinner } from "../common/LoadingState";

interface TeamsIntegrationStatus {
  active_bindings_count: number;
  active_installations_count: number;
  archived_channels_count: number;
  is_admin: boolean;
  status: "active" | "inactive";
}

type TeamsIntegrationState =
  | { status: "loading" }
  | { status: "ready"; detail: TeamsIntegrationStatus }
  | { status: "error"; message: string };

export const TeamsIntegrationPanel: FC = () => {
  const { globalRole } = useDashboardAuth();
  const [state, setState] = useState<TeamsIntegrationState>({
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch("/user/teams/status");
        if (!response.ok) {
          throw new Error("Failed to load Teams status.");
        }
        const detail = parseTeamsIntegrationStatus(await response.json());
        if (!cancelled) {
          setState({ detail, status: "ready" });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            message:
              error instanceof Error
                ? error.message
                : "Failed to load Teams status.",
            status: "error",
          });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (state.status === "error") {
    return <Alert variant="warning">{state.message}</Alert>;
  }

  const isAdmin = state.detail.is_admin || globalRole === "admin";
  return <TeamsIntegrationCard detail={state.detail} isAdmin={isAdmin} />;
};

export const TeamsIntegrationCard: FC<{
  detail: TeamsIntegrationStatus;
  isAdmin: boolean;
}> = ({ detail, isAdmin }) => (
  <section className="space-y-4 border border-slate-800 bg-slate-950/60 p-4 font-mono text-sm text-slate-300">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-cyan-300/80">
          [ MICROSOFT TEAMS ]
        </p>
        <h2 className="mt-2 text-lg font-semibold text-slate-100">
          Teams chat integration
        </h2>
        <p className="mt-1 max-w-2xl text-xs text-slate-400">
          Connect Microsoft Teams chats and channels to ARTI shared channels.
        </p>
      </div>
      <span
        className={`border px-2 py-1 text-[10px] uppercase tracking-[0.2em] ${
          detail.status === "active"
            ? "border-emerald-400/40 text-emerald-300"
            : "border-slate-700 text-slate-500"
        }`}
      >
        {detail.status}
      </span>
    </div>

    <div className="grid gap-2 sm:grid-cols-3">
      <Metric label="active bindings" value={detail.active_bindings_count} />
      <Metric
        label="active installations"
        value={detail.active_installations_count}
      />
      <Metric
        label="archived channels"
        value={detail.archived_channels_count}
      />
    </div>

    <div className="border border-slate-800 bg-slate-900/50 p-3 text-xs text-slate-400">
      {isAdmin ? (
        <a
          className="inline-block border border-cyan-400/40 px-3 py-2 text-cyan-300 transition-colors hover:bg-cyan-400/10"
          href="/admin?tab=teams"
        >
          [ OPEN ADMIN TEAMS SETUP ]
        </a>
      ) : (
        <p>Contact your platform admin to configure Microsoft Teams.</p>
      )}
    </div>
  </section>
);

const Metric: FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="border border-slate-800 bg-slate-900/50 p-3">
    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
      {label}
    </p>
    <p className="mt-1 text-lg text-slate-100">{value}</p>
  </div>
);

function parseTeamsIntegrationStatus(value: unknown): TeamsIntegrationStatus {
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid Teams status response.");
  }
  return {
    active_bindings_count: readNumber(
      readProperty(value, "active_bindings_count"),
    ),
    active_installations_count: readNumber(
      readProperty(value, "active_installations_count"),
    ),
    archived_channels_count: readNumber(
      readProperty(value, "archived_channels_count"),
    ),
    is_admin: readProperty(value, "is_admin") === true,
    status: readProperty(value, "status") === "active" ? "active" : "inactive",
  };
}

function readProperty(value: object, key: string): unknown {
  return Object.getOwnPropertyDescriptor(value, key)?.value ?? null;
}

function readNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
